import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { meetingsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Mic,
  Plus,
  Search,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileText,
  ListTodo,
  Sparkles,
  ArrowRight,
  Globe,
  MessageSquare,
  Trash2,
} from 'lucide-react';
import { format } from 'date-fns';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "") + '/api';

const LANGUAGES = {
  pl: { name: 'Polski', flag: '🇵🇱' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  en: { name: 'English', flag: '🇬🇧' },
  es: { name: 'Español', flag: '🇪🇸' },
  fr: { name: 'Français', flag: '🇫🇷' },
  it: { name: 'Italiano', flag: '🇮🇹' },
  uk: { name: 'Українська', flag: '🇺🇦' },
  ru: { name: 'Русский', flag: '🇷🇺' },
  zh: { name: '中文', flag: '🇨🇳' },
  ja: { name: '日本語', flag: '🇯🇵' },
  ko: { name: '한국어', flag: '🇰🇷' },
  ar: { name: 'العربية', flag: '🇸🇦' },
  pt: { name: 'Português', flag: '🇵🇹' },
  nl: { name: 'Nederlands', flag: '🇳🇱' },
  tr: { name: 'Türkçe', flag: '🇹🇷' },
  cs: { name: 'Čeština', flag: '🇨🇿' },
};

const StatusBadge = ({ status, t }) => {
  const variants = {
    pending: { variant: 'secondary', icon: Clock, label: t('dashboard.status.pending') },
    processing: { variant: 'outline', icon: Loader2, label: t('dashboard.status.processing'), animate: true },
    completed: { variant: 'default', icon: CheckCircle, label: t('dashboard.status.completed') },
    active: { variant: 'outline', icon: Globe, label: 'Active' },
    error: { variant: 'destructive', icon: AlertCircle, label: t('dashboard.status.error') },
  };

  const config = variants[status] || variants.pending;
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className="gap-1">
      <Icon className={`w-3 h-3 ${config.animate ? 'animate-spin' : ''}`} />
      {config.label}
    </Badge>
  );
};

const MeetingCard = ({ meeting, t, onDelete }) => {
  const actionItemsCount = meeting.action_items?.length || 0;
  const completedActions = meeting.action_items?.filter((a) => a.completed).length || 0;

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm(t('dashboard.confirmDelete'))) {
      onDelete(meeting.id);
    }
  };

  return (
    <Link to={`/meeting/${meeting.id}`} data-testid={`meeting-card-${meeting.id}`}>
      <Card className="h-full bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-violet-500/50 transition-all duration-300 cursor-pointer group">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-lg font-semibold line-clamp-1 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
              {meeting.title}
            </CardTitle>
            <div className="flex items-center gap-2">
              <StatusBadge status={meeting.status} t={t} />
              <button
                onClick={handleDelete}
                className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors opacity-0 group-hover:opacity-100"
                title={t('dashboard.delete')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
          {meeting.description && (
            <CardDescription className="line-clamp-2">{meeting.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Date */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>{format(new Date(meeting.created_at), 'PPP')}</span>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4">
              {meeting.status === 'completed' && (
                <>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <FileText className="w-4 h-4" />
                    <span>{meeting.transcript?.length || 0} segments</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                    <ListTodo className="w-4 h-4" />
                    <span>
                      {completedActions}/{actionItemsCount} {t('dashboard.card.actionItems')}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Preview of summary */}
            {meeting.summary && (
              <p className="text-sm text-muted-foreground line-clamp-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                {meeting.summary}
              </p>
            )}

            {/* View details link */}
            <div className="flex items-center gap-1 text-sm font-medium text-violet-600 dark:text-violet-400 group-hover:gap-2 transition-all">
              {t('dashboard.card.viewDetails')}
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const LiveSessionCard = ({ session, t }) => {
  const actionItemsCount = session.action_items?.length || 0;
  const completedActions = session.action_items?.filter((a) => a.completed).length || 0;
  const messagesCount = session.messages?.length || 0;

  return (
    <Link to={`/live/session/${session.id}`} data-testid={`live-session-card-${session.id}`}>
      <Card className="h-full bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-emerald-500/50 transition-all duration-300 cursor-pointer group">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-lg font-semibold line-clamp-1 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors flex items-center gap-2">
              <Globe className="w-5 h-5 text-emerald-600" />
              {session.title || `Session ${session.id}`}
            </CardTitle>
            <StatusBadge status={session.status} t={t} />
          </div>
          {session.description && (
            <CardDescription className="line-clamp-2">{session.description}</CardDescription>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Date & Languages */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="w-4 h-4" />
              <span>{format(new Date(session.created_at), 'PPP')}</span>
              <span className="ml-auto">
                {LANGUAGES[session.language1]?.flag} ↔ {LANGUAGES[session.language2]?.flag}
              </span>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <MessageSquare className="w-4 h-4" />
                <span>{messagesCount} messages</span>
              </div>
              {actionItemsCount > 0 && (
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  <ListTodo className="w-4 h-4" />
                  <span>
                    {completedActions}/{actionItemsCount} {t('dashboard.card.actionItems')}
                  </span>
                </div>
              )}
            </div>

            {/* Preview of summary */}
            {session.summary && (
              <p className="text-sm text-muted-foreground line-clamp-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                {session.summary}
              </p>
            )}

            {/* View details link */}
            <div className="flex items-center gap-1 text-sm font-medium text-emerald-600 dark:text-emerald-400 group-hover:gap-2 transition-all">
              {t('dashboard.card.viewDetails')}
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const Dashboard = () => {
  const { t, token } = useApp();
  const navigate = useNavigate();
  const [meetings, setMeetings] = useState([]);
  const [liveSessions, setLiveSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [creatingDemo, setCreatingDemo] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [meetingsData, sessionsRes] = await Promise.all([
        meetingsAPI.getAll(),
        fetch(`${API_BASE}/live/sessions`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      setMeetings(meetingsData);
      
      if (sessionsRes.ok) {
        const sessionsData = await sessionsRes.json();
        setLiveSessions(sessionsData);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDemo = async () => {
    setCreatingDemo(true);
    try {
      const newMeeting = await meetingsAPI.createDemo();
      setMeetings([newMeeting, ...meetings]);
      toast.success('Demo meeting created!');
    } catch (error) {
      toast.error('Failed to create demo meeting');
    } finally {
      setCreatingDemo(false);
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    try {
      await meetingsAPI.delete(meetingId);
      setMeetings(meetings.filter(m => m.id !== meetingId));
      toast.success(t('dashboard.deleteSuccess'));
    } catch (error) {
      toast.error(t('dashboard.deleteError'));
    }
  };

  const filteredMeetings = meetings.filter((m) =>
    m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSessions = liveSessions.filter((s) =>
    (s.title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (s.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Combine all items for "All" tab
  const allItems = [
    ...filteredMeetings.map(m => ({ ...m, type: 'meeting' })),
    ...filteredSessions.map(s => ({ ...s, type: 'live' }))
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const renderEmptyState = () => (
    <Card className="max-w-lg mx-auto text-center py-16 rounded-2xl" data-testid="empty-state">
      <CardContent>
        <div className="w-20 h-20 rounded-2xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mx-auto mb-6">
          <Mic className="w-10 h-10 text-violet-600 dark:text-violet-400" />
        </div>
        <h3 className="text-xl font-semibold mb-2">{t('dashboard.noMeetings')}</h3>
        <p className="text-muted-foreground mb-6">{t('dashboard.noMeetingsDesc')}</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={() => navigate('/recording')}
            className="rounded-full bg-violet-600 hover:bg-violet-700"
            data-testid="start-recording-button"
          >
            <Mic className="w-4 h-4 mr-2" />
            {t('dashboard.newMeeting')}
          </Button>
          <Button
            onClick={() => navigate('/live')}
            variant="outline"
            className="rounded-full"
            data-testid="start-live-button"
          >
            <Globe className="w-4 h-4 mr-2" />
            {t('nav.liveTranslation')}
          </Button>
          <Button
            onClick={handleCreateDemo}
            variant="outline"
            className="rounded-full"
            disabled={creatingDemo}
            data-testid="create-demo-button"
          >
            {creatingDemo ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            {t('dashboard.createDemo')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight" data-testid="dashboard-title">
              {t('dashboard.title')}
            </h1>
            <p className="text-muted-foreground mt-1">{t('dashboard.subtitle')}</p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/live')}
              variant="outline"
              className="rounded-full px-6 h-11"
              data-testid="live-translation-button"
            >
              <Globe className="w-5 h-5 mr-2" />
              {t('nav.liveTranslation')}
            </Button>
            <Button
              onClick={() => navigate('/recording')}
              className="rounded-full bg-violet-600 hover:bg-violet-700 px-6 h-11 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              data-testid="new-meeting-button"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t('dashboard.newMeeting')}
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t('dashboard.search')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 h-12 rounded-xl text-base"
            data-testid="dashboard-search-input"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="rounded-xl h-11">
            <TabsTrigger value="all" className="rounded-lg px-6" data-testid="tab-all">
              All ({allItems.length})
            </TabsTrigger>
            <TabsTrigger value="meetings" className="rounded-lg px-6" data-testid="tab-meetings">
              <Mic className="w-4 h-4 mr-2" />
              Meetings ({filteredMeetings.length})
            </TabsTrigger>
            <TabsTrigger value="live" className="rounded-lg px-6" data-testid="tab-live">
              <Globe className="w-4 h-4 mr-2" />
              Live Sessions ({filteredSessions.length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Content */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="rounded-2xl">
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <>
            {/* All Tab */}
            {activeTab === 'all' && (
              allItems.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="all-items-grid">
                  {allItems.map((item) => (
                    item.type === 'meeting' ? (
                      <MeetingCard key={`meeting-${item.id}`} meeting={item} t={t} onDelete={handleDeleteMeeting} />
                    ) : (
                      <LiveSessionCard key={`live-${item.id}`} session={item} t={t} />
                    )
                  ))}
                </div>
              ) : renderEmptyState()
            )}

            {/* Meetings Tab */}
            {activeTab === 'meetings' && (
              filteredMeetings.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="meetings-grid">
                  {filteredMeetings.map((meeting) => (
                    <MeetingCard key={meeting.id} meeting={meeting} t={t} onDelete={handleDeleteMeeting} />
                  ))}
                </div>
              ) : (
                <Card className="max-w-lg mx-auto text-center py-16 rounded-2xl">
                  <CardContent>
                    <Mic className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-xl font-semibold mb-2">No meetings yet</h3>
                    <p className="text-muted-foreground mb-6">Record or upload your first meeting</p>
                    <Button
                      onClick={() => navigate('/recording')}
                      className="rounded-full bg-violet-600 hover:bg-violet-700"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {t('dashboard.newMeeting')}
                    </Button>
                  </CardContent>
                </Card>
              )
            )}

            {/* Live Sessions Tab */}
            {activeTab === 'live' && (
              filteredSessions.length > 0 ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="live-sessions-grid">
                  {filteredSessions.map((session) => (
                    <LiveSessionCard key={session.id} session={session} t={t} />
                  ))}
                </div>
              ) : (
                <Card className="max-w-lg mx-auto text-center py-16 rounded-2xl">
                  <CardContent>
                    <Globe className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-xl font-semibold mb-2">No live sessions yet</h3>
                    <p className="text-muted-foreground mb-6">Start a live translation session</p>
                    <Button
                      onClick={() => navigate('/live')}
                      className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Globe className="w-4 h-4 mr-2" />
                      {t('nav.liveTranslation')}
                    </Button>
                  </CardContent>
                </Card>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
